import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ElementRef,
  ViewChild,
} from "@angular/core";
import LoggerService from "@bfx/logger";
const _logger = LoggerService.for(__dirname);

/**
 * Component wrapping all Design Specs for an Icon Button.
 * Support the following:
 *    intent: "primary" | "secondary" | "success" | "danger" | "inverse" | "buy" | "sell"
 *    emphasis: "bold" | "moderate" | "subtle";
 *    size: xs" | "sm" | "md" | "lg"
 *
 * Click actions are emitted as action events.
 */

type IntentType = "primary" | "secondary" | "positive" | "negative" | "inverse" | "buy" | "sell";
type EmphasisType = "bold" | "moderate" | "subtle";
type SizeType = "xs" | "sm" | "md" | "lg";

@Component({
  selector: "bfx-icon-button",
  templateUrl: "./html/bfx-icon-button.html",
  standalone: false,
})
export class BfxIconButtonComponent implements OnChanges {
  /** Intent  e.g. primary, secondary etc */
  @Input() intent: IntentType = "secondary";
  /** Emphasis e.g. bold, subtle etc */
  @Input() emphasis: EmphasisType = "subtle";

  /** Size - e.g. sm, md */
  @Input() size: SizeType = "md";

  /** If defined then the size of the icon button will be one size smaller than the parentSize */
  @Input() parentSize: SizeType = null;

  /** Class for icon  */
  @Input() iconClass: string = "";

  @Input() title: string = "";

  @Input() disabled: boolean = false;

  /** By default, the button will not participate in tabbing unless enableTabbing is set to true */
  @Input() enableTabbing: boolean = false;

  /** Action executed on click */
  @Output() action: EventEmitter<PointerEvent> = new EventEmitter();

  /** Outputs mouse event from internal button on blue */
  @Output() componentBlur: EventEmitter<MouseEvent> = new EventEmitter();

  /** Outputs mouse event from internal button on focus */
  @Output() componentFocus: EventEmitter<MouseEvent> = new EventEmitter();

  @ViewChild("button") private buttonRef: ElementRef;

  /**
   * Derived properties
   */
  buttonClasses: string;

  constructor() {
    //
  }

  /**
   * Update classes after any input changes
   * @param _changes
   */
  ngOnChanges(_changes: SimpleChanges): void {
    // create button class
    const dispClass = this.createUnsizedButtonClass();
    const sizeClass = this.createButtonSizeClass();

    const tabbingClass = this.enableTabbing ? "" : "hide-focus";
    this.buttonClasses = `${tabbingClass} ${dispClass} ${sizeClass}`;
  }

  /**
   * Create class for unscaled style
   * @returns
   */
  private createUnsizedButtonClass(): string {
    return `bfx-icon-button-${this.intent}-${this.emphasis}-unsized`;
  }

  /**
   *  Create size class
   * @returns
   */
  private createButtonSizeClass(): string {
    const relativeSize = this.parentSize ? this.createSizeFromParentSize() : this.size;
    return `bfx-icon-button-${relativeSize}`;
  }
  createSizeFromParentSize() {
    switch (this.parentSize) {
      case "md":
        return "sm";
      case "lg":
        return "md";
      default:
        return "xs";
    }
  }

  /**
   *
   * @param event
   */
  public clickAction(event: PointerEvent): void {
    this.action.emit(event);
  }

  /**
   * Redispatch the inner MouseEvent - it can be associated with this component using the buttonElement getter
   * @param event
   */
  public onBlur(event: MouseEvent) {
    this.componentBlur.emit(event);
  }

  /**
   * Redispatch the inner MouseEvent - it can be associated with this component using the buttonElement getter
   * @param event
   */
  public onFocus(event: MouseEvent) {
    this.componentFocus.emit(event);
  }

  /**
   * The button element for all mouse events
   */
  public get buttonElement(): ElementRef {
    return this.buttonRef;
  }
}
